import * as THREE from 'three';
import type { ExperienceCue, ExperienceManifest, ThoughtIdentityConfig } from '../content/types';
import { activeCueAt } from '../content/types';
import { ThoughtAudioFeedback } from './ThoughtAudioFeedback';

export interface MediaSnapshot {
  time: number;
  duration: number;
  playing: boolean;
  seeking: boolean;
  ready: boolean;
  cue: ExperienceCue | null;
  muted: boolean;
  error: string | null;
}

type Listener = (snapshot: MediaSnapshot) => void;

export class MediaDirector {
  readonly panoramaTexture: THREE.CanvasTexture;
  readonly thoughtFeedback = new ThoughtAudioFeedback();
  private readonly frameCanvas = document.createElement('canvas');
  private readonly frameContext: CanvasRenderingContext2D;
  private readonly listeners = new Set<Listener>();
  private readonly holdFrames = new Map<string, HTMLImageElement>();
  private manifest!: ExperienceManifest;
  private cue: ExperienceCue | null = null;
  private ready = false;
  private error: string | null = null;
  private muted = false;
  private audioContext?: AudioContext;
  private analyser?: AnalyserNode;
  private masterGain?: GainNode;
  private waveformData?: Float32Array<ArrayBuffer>;
  private frameCallback = 0;
  private disposed = false;
  private resumeAfterVisibility = false;

  constructor(private readonly video: HTMLVideoElement) {
    // The browser presentation is a rectilinear 16:9 extraction of the 8K
    // equirectangular master. Keep the upload texture at display-class
    // resolution so a 60 fps canvas copy remains practical on laptops.
    this.frameCanvas.width = 1920;
    this.frameCanvas.height = 1080;
    const context = this.frameCanvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('A 2D canvas is required for the panorama hold system.');
    this.frameContext = context;
    this.paintFallback();
    this.panoramaTexture = new THREE.CanvasTexture(this.frameCanvas);
    this.panoramaTexture.colorSpace = THREE.SRGBColorSpace;
    this.panoramaTexture.minFilter = THREE.LinearFilter;
    this.panoramaTexture.magFilter = THREE.LinearFilter;
    this.panoramaTexture.generateMipmaps = false;
    this.video.addEventListener('loadedmetadata', this.handleMetadata);
    this.video.addEventListener('loadeddata', this.handleLoadedData);
    this.video.addEventListener('play', this.emit);
    this.video.addEventListener('pause', this.emit);
    this.video.addEventListener('seeking', this.handleSeeking);
    this.video.addEventListener('seeked', this.emit);
    this.video.addEventListener('timeupdate', this.updateCue);
    this.video.addEventListener('ended', this.emit);
    this.video.addEventListener('error', this.handleError);
    document.addEventListener('visibilitychange', this.handleVisibility);
  }

  async load(manifest: ExperienceManifest): Promise<void> {
    this.manifest = manifest;
    const base = import.meta.env.BASE_URL;
    const assets = [manifest.media.poster, ...manifest.cues.map((cue) => cue.holdFrame)];
    await Promise.all(assets.map(async (path) => {
      try {
        this.holdFrames.set(path, await loadImage(`${base}${path}`));
      } catch {
        // A missing still degrades to the most recent good panorama, never to black.
      }
    }));
    const poster = this.holdFrames.get(manifest.media.poster);
    if (poster) this.drawImage(poster);
    this.video.src = `${base}${manifest.media.master}`;
    this.video.load();
    this.scheduleVideoFrame();
    this.emit();
  }

  onChange(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  configureThoughtIdentity(config: ThoughtIdentityConfig): void { this.thoughtFeedback.configure(config); }

  get snapshot(): MediaSnapshot {
    const duration = Number.isFinite(this.video.duration) ? this.video.duration : (this.manifest?.media.durationSeconds ?? 0);
    return {
      time: Number.isFinite(this.video.currentTime) ? this.video.currentTime : 0,
      duration,
      playing: !this.video.paused && !this.video.ended,
      seeking: this.video.seeking,
      ready: this.ready,
      cue: this.cue,
      muted: this.muted,
      error: this.error,
    };
  }

  async play(): Promise<void> {
    await this.unlockAudio();
    await this.video.play();
    this.emit();
  }

  pause(): void {
    this.resumeAfterVisibility = false;
    this.video.pause();
    this.emit();
  }

  async toggle(): Promise<void> {
    if (this.video.paused) await this.play(); else this.pause();
  }

  seek(time: number): void {
    const duration = this.snapshot.duration || this.manifest.media.durationSeconds;
    this.video.currentTime = Math.max(0, Math.min(duration, time));
    this.updateCue();
  }

  restart(): void {
    this.seek(0);
  }

  seekCue(direction: 1 | -1): void {
    const time = this.video.currentTime;
    const cues = this.manifest.cues;
    if (direction > 0) {
      const next = cues.find((cue) => cue.start > time + 0.25);
      if (next) this.seek(next.start + 0.04);
    } else {
      const previous = [...cues].reverse().find((cue) => cue.start < time - 0.8) ?? cues[0];
      this.seek(previous.start + 0.04);
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.video.muted = muted;
    if (this.masterGain && this.audioContext) {
      this.masterGain.gain.setTargetAtTime(muted ? 0 : 1, this.audioContext.currentTime, 0.025);
    }
    this.emit();
  }

  getAudioEnergy(): number {
    if (this.muted || this.video.paused || this.video.ended) return 0;
    if (!this.analyser || !this.waveformData) return 0;
    this.analyser.getFloatTimeDomainData(this.waveformData);
    let total = 0;
    for (const sample of this.waveformData) total += sample * sample;
    return Math.min(1, Math.max(0, (Math.sqrt(total / this.waveformData.length) - 0.004) * 8));
  }

  getPlaybackQuality(): { dropped: number; total: number } {
    const quality = this.video.getVideoPlaybackQuality?.();
    return { dropped: quality?.droppedVideoFrames ?? 0, total: quality?.totalVideoFrames ?? 0 };
  }

  dispose(): void {
    this.disposed = true;
    this.thoughtFeedback.stop();
    if (this.frameCallback && 'cancelVideoFrameCallback' in this.video) this.video.cancelVideoFrameCallback(this.frameCallback);
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
    this.panoramaTexture.dispose();
    void this.audioContext?.close();
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  private readonly handleMetadata = (): void => {
    this.ready = true;
    this.error = null;
    this.emit();
  };

  private readonly handleLoadedData = (): void => {
    if (!this.cue) this.drawVideoFrame();
    this.emit();
  };

  private readonly handleSeeking = (): void => {
    this.thoughtFeedback.stop();
    this.updateCue();
    if (this.cue) this.drawHold(this.cue);
  };

  private readonly handleError = (): void => {
    this.error = this.video.error?.message || 'The browser could not decode the immersive video.';
    this.ready = false;
    this.emit();
  };

  private readonly handleVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      this.resumeAfterVisibility = !this.video.paused;
      this.video.pause();
    } else if (this.resumeAfterVisibility) {
      this.resumeAfterVisibility = false;
      void this.play().catch((error: unknown) => {
        this.error = error instanceof Error ? error.message : 'Select Play to resume the encounter.';
        this.emit();
      });
    }
  };

  private readonly updateCue = (): void => {
    if (!this.manifest) return;
    const next = activeCueAt(this.manifest, this.video.currentTime);
    if (next?.id !== this.cue?.id) {
      this.cue = next;
      if (next) this.drawHold(next); else this.drawVideoFrame();
    }
    this.emit();
  };

  private readonly emit = (): void => {
    const state = this.snapshot;
    this.thoughtFeedback.update(state);
    for (const listener of this.listeners) listener(state);
  };

  private scheduleVideoFrame(): void {
    const onFrame = () => {
      if (this.disposed) return;
      this.updateCue();
      if (!this.cue) this.drawVideoFrame();
      this.scheduleVideoFrame();
    };
    if ('requestVideoFrameCallback' in this.video) {
      this.frameCallback = this.video.requestVideoFrameCallback(onFrame);
    } else {
      window.setTimeout(onFrame, 1000 / 24);
    }
  }

  private drawVideoFrame(): void {
    if (this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || this.video.videoWidth === 0) return;
    this.frameContext.drawImage(this.video, 0, 0, this.frameCanvas.width, this.frameCanvas.height);
    this.panoramaTexture.needsUpdate = true;
  }

  private drawHold(cue: ExperienceCue): void {
    const image = this.holdFrames.get(cue.holdFrame);
    if (image) this.drawImage(image);
  }

  private drawImage(image: CanvasImageSource): void {
    this.frameContext.drawImage(image, 0, 0, this.frameCanvas.width, this.frameCanvas.height);
    this.panoramaTexture.needsUpdate = true;
  }

  private paintFallback(): void {
    const gradient = this.frameContext.createLinearGradient(0, 0, this.frameCanvas.width, this.frameCanvas.height);
    gradient.addColorStop(0, '#2e4142');
    gradient.addColorStop(0.52, '#d9d7c7');
    gradient.addColorStop(1, '#6a7b75');
    this.frameContext.fillStyle = gradient;
    this.frameContext.fillRect(0, 0, this.frameCanvas.width, this.frameCanvas.height);
  }

  private async unlockAudio(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
      const source = this.audioContext.createMediaElementSource(this.video);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 1024;
      this.analyser.smoothingTimeConstant = 0.82;
      this.masterGain = this.audioContext.createGain();
      this.masterGain.gain.value = this.muted ? 0 : 1;
      source.connect(this.analyser).connect(this.masterGain).connect(this.audioContext.destination);
      this.waveformData = new Float32Array(this.analyser.fftSize);
      // Tones bypass the voice analyser so they cannot drive the speaker halo.
      this.thoughtFeedback.connect(this.audioContext, this.masterGain);
    }
    if (this.audioContext.state !== 'running') await this.audioContext.resume();
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Unable to load ${src}`));
    image.src = src;
  });
}
